import store from '../store/index.js'

/**
 * 平台里程/速度单位换算工具。
 * 数据源始终是公制：speed 为 km/h，journeyStr/journey/odometers 为 km（journey 为米）。
 * 用户在 平台设置-通用 里选了 mile(value=2)/mileHour(value=2) 时，仅在展示层换算：
 *   1 mile = 1.609344 km
 * 单位标签与新前端 MS06_Next 保持一致：km 模式沿用历史字面量 "km"/"km/h"，
 * mile 模式用国际通用缩写 "miles"/"mph"。
 */
const KM_PER_MILE = 1.609344

function getMgSetting() {
  return store.getters.MGSettingData || store.getters.MGSettingDefaultData || {}
}

export function isMileUnit() {
  return getMgSetting().mileageUnitSelected === 2
}

export function isMphUnit() {
  return getMgSetting().speedUnitSelected === 2
}

/** 里程单位标签：km 模式返回字面量 "km"，mile 模式返回 "miles" */
export function distanceUnitLabel() {
  return isMileUnit() ? 'miles' : 'km'
}

/** 速度单位标签：km 模式返回字面量 "km/h"，mile 模式返回 "mph" */
export function speedUnitLabel() {
  return isMphUnit() ? 'mph' : 'km/h'
}

/** km/h 数值 → 当前速度单位数值（取整，与既有速度展示一致） */
export function convertSpeed(kmh) {
  const v = Number(kmh)
  if (!isFinite(v)) return 0
  return isMphUnit() ? Math.round(v / KM_PER_MILE) : v
}

/** km 数值 → 当前里程单位数值，digits 为小数位（默认 1，与后端 journeyStr 格式一致） */
export function convertDistance(km, digits = 1) {
  const v = Number(km)
  if (!isFinite(v)) return 0
  const out = isMileUnit() ? v / KM_PER_MILE : v
  return Number(out.toFixed(digits))
}

/** 速度值 + 单位，如 "12km/h" / "7mile/h"，替代散落各处的 `speed + 'km/h'` */
export function speedText(kmh) {
  return convertSpeed(kmh) + speedUnitLabel()
}

/** km 值 + 单位，如 "123.5km" / "76.7mile" */
export function distanceText(km, digits = 1) {
  return convertDistance(km, digits) + distanceUnitLabel()
}

/**
 * 后端 lastLoc.journeyStr（"1234.5km"）→ 当前单位的 "数值+单位"。
 * 解析失败时原样返回，避免异常数据导致界面空白。
 */
export function journeyText(journeyStr) {
  const v = parseFloat(journeyStr)
  if (!isFinite(v)) return journeyStr
  return distanceText(v)
}

/** 后端 lastLoc.journeyStr → 当前单位的纯数值（供数值与单位分开展示的组件） */
export function journeyValue(journeyStr) {
  const v = parseFloat(journeyStr)
  if (!isFinite(v)) return 0
  return convertDistance(v)
}

/**
 * 表格行展示层单位转换：speed(km/h 数值)→当前速度单位数值、journeyStr("12.3km")→当前单位文本。
 * 返回浅拷贝行对象，不改原对象——轨迹点/实时位置数据同时用于播放着色、地图 marker 等逻辑，必须保持公制。
 * km 模式输出与原值一致（speed 原数值、journeyStr 保持 "x.xkm" 一位小数格式）。
 */
export function convertTableRowUnits(row) {
  if (!row || typeof row !== 'object') return row
  const out = { ...row }
  if (out.speed !== undefined && out.speed !== null && out.speed !== '') {
    const v = Number(out.speed)
    if (isFinite(v)) out.speed = convertSpeed(v)
  }
  if (typeof out.journeyStr === 'string' && out.journeyStr) {
    out.journeyStr = journeyText(out.journeyStr)
  }
  return out
}
